"use client"

import * as React from "react"
import { X } from "lucide-react"
import { cn } from '@/lib/core/utils'

interface Toast {
  id: string
  message: string
  type?: 'success' | 'error' | 'info'
  action?: {
    label: string
    onClick: () => void
  }
}

const ToastContext = React.createContext<{
  toasts: Toast[]
  addToast: (toast: Omit<Toast, 'id'>) => void
  removeToast: (id: string) => void
}>({
  toasts: [],
  addToast: () => {},
  removeToast: () => {},
})

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = React.useState<Toast[]>([])

  const addToast = React.useCallback((toast: Omit<Toast, 'id'>) => {
    const id = Math.random().toString(36).substring(7)
    setToasts((prev) => [...prev, { ...toast, id }])
    
    // Auto-remove after 5 seconds
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id))
    }, 5000)
  }, [])

  const removeToast = React.useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id))
  }, [])

  return (
    <ToastContext.Provider value={{ toasts, addToast, removeToast }}>
      {children}
      <div className="fixed bottom-4 right-4 z-[9999] flex flex-col gap-1.5 pointer-events-none">
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={cn(
              "pointer-events-auto min-w-[200px] max-w-[280px] rounded-lg border p-2.5 shadow-xl animate-in slide-in-from-bottom-4",
              "bg-white dark:bg-gray-800 border-[#DEDAD2] dark:border-gray-700",
              toast.type === 'success' && "border-l-[3px] border-l-[#FF6B35] dark:border-l-orange-500",
              toast.type === 'error' && "border-l-[3px] border-l-red-500 dark:border-l-red-400",
              toast.type === 'info' && "border-l-[3px] border-l-[#FF6B35] dark:border-l-orange-400"
            )}
          >
            <div className="flex items-start justify-between gap-2">
              <p className="text-xs text-[#1F1F1F] dark:text-gray-100 flex-1 leading-snug">{toast.message}</p>
              <button
                onClick={() => removeToast(toast.id)}
                className="text-[#6B6B6B] dark:text-gray-400 hover:text-[#1F1F1F] dark:hover:text-gray-100 transition-smooth flex-shrink-0"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
            {toast.action && (
              <button
                onClick={() => {
                  toast.action!.onClick()
                  removeToast(toast.id)
                }}
                className="mt-1.5 text-xs font-medium text-[#FF6B35] dark:text-orange-400 hover:text-[#E55A2B] dark:hover:text-orange-300 transition-smooth"
              >
                {toast.action.label}
              </button>
            )}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  )
}

export function useToast() {
  const context = React.useContext(ToastContext)
  if (!context) {
    throw new Error('useToast must be used within ToastProvider')
  }
  return context
}

















